/*
 * Iter 31.308 — Gemini chat page.
 *
 * Peer surface to Number 2 (Claude). Streaming responses via SSE,
 * session sidebar, transcript persistence in Mongo. Route mounted
 * at /chat/gemini in App.js.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, getToken } from "@/lib/api";
import { Loader2, Send, Plus, Trash2, Sparkles, MessageSquare, ArrowRightCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GeminiChat() {
    const [sessions, setSessions] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [pending, setPending] = useState("");   // in-flight assistant text
    const [err, setErr] = useState("");
    // Iter 31.315 — handoff-to-Two state.
    const [handoffState, setHandoffState] = useState(null); // {loading|ok|err, handoff_id?, msg?}
    const scrollRef = useRef(null);

    const reloadSessions = useCallback(async () => {
        try {
            const { data } = await api.get("/chat/gemini/sessions");
            setSessions(data?.sessions || []);
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    }, []);

    useEffect(() => { reloadSessions(); }, [reloadSessions]);

    const loadSession = useCallback(async (id) => {
        try {
            const { data } = await api.get(`/chat/gemini/sessions/${id}`);
            setActiveId(id);
            setMessages(data?.messages || []);
            setPending("");
            setHandoffState(null);
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    }, []);

    // Iter 31.315 — file the current transcript as a `gemini_handoff`
    // ledger entry so Two can pick it up via MCP without you having
    // to re-type the context in a separate Claude window.
    const handoff = useCallback(async () => {
        if (!activeId || messages.length === 0 || handoffState?.loading) return;
        setHandoffState({ loading: true });
        try {
            const { data } = await api.post(`/chat/gemini/sessions/${activeId}/handoff`);
            setHandoffState({
                ok: true,
                handoff_id: data?.handoff_id,
                turn_count: data?.turn_count,
                msg: data?.instructions_for_two,
            });
        } catch (e) {
            setHandoffState({
                err: true,
                msg: e?.response?.data?.detail || e.message,
            });
        }
    }, [activeId, messages.length, handoffState?.loading]);

    const newSession = useCallback(async () => {
        try {
            const { data } = await api.post("/chat/gemini/sessions", { title: "New chat" });
            const id = data?.session?.id;
            await reloadSessions();
            if (id) loadSession(id);
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    }, [reloadSessions, loadSession]);

    const deleteSession = useCallback(async (id) => {
        if (!window.confirm("Delete this chat?")) return;
        try {
            await api.delete(`/chat/gemini/sessions/${id}`);
            if (activeId === id) { setActiveId(null); setMessages([]); }
            reloadSessions();
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    }, [activeId, reloadSessions]);

    const send = useCallback(async () => {
        if (!input.trim() || !activeId || streaming) return;
        const text = input.trim();
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text, id: `local-${Date.now()}` }]);
        setStreaming(true);
        setPending("");
        setErr("");
        try {
            const token = getToken();
            const resp = await fetch(
                `${API}/api/chat/gemini/sessions/${activeId}/stream`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ text }),
                },
            );
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let acc = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buffer.indexOf("\n\n")) !== -1) {
                    const chunk = buffer.slice(0, idx).trim();
                    buffer = buffer.slice(idx + 2);
                    if (!chunk.startsWith("data:")) continue;
                    const payload = chunk.slice(5).trim();
                    if (payload === "[DONE]") continue;
                    try {
                        const j = JSON.parse(payload);
                        if (j.delta) { acc += j.delta; setPending(acc); }
                        if (j.error) setErr(j.error);
                    } catch { /* ignore malformed SSE frame */ }
                }
            }
            setMessages((m) => [
                ...m,
                { role: "assistant", content: acc, id: `local-a-${Date.now()}` },
            ]);
            setPending("");
            reloadSessions();
        } catch (e) {
            setErr(e.message);
        } finally {
            setStreaming(false);
        }
    }, [input, activeId, streaming, reloadSessions]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, pending]);

    return (
        <div className="flex h-[calc(100vh-4rem)]" data-testid="gemini-chat-root">
            {/* Session sidebar */}
            <aside className="w-64 border-r border-ink/15 flex flex-col shrink-0" data-testid="gemini-sidebar">
                <div className="p-3 border-b border-ink/15">
                    <button
                        type="button"
                        onClick={newSession}
                        data-testid="gemini-new-session"
                        className="w-full font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 inline-flex items-center justify-center gap-1.5"
                    >
                        <Plus className="w-3 h-3" /> New chat
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {sessions.length === 0 ? (
                        <div className="p-3 font-body text-[11px] text-ink-faint italic">
                            No chats yet. Start one above.
                        </div>
                    ) : (
                        sessions.map((s) => (
                            <div
                                key={s.id}
                                onClick={() => loadSession(s.id)}
                                data-testid={`gemini-session-${s.id}`}
                                className={`p-3 border-b border-ink/10 cursor-pointer group flex items-start justify-between gap-2 ${activeId === s.id ? "bg-stamp-red/5 border-l-2 border-l-stamp-red" : "hover:bg-ink/5"}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="font-body text-[11px] text-ink truncate">{s.title || "Untitled"}</div>
                                    <div className="font-mono text-[9px] text-ink-faint mt-0.5">{s.updated_at?.slice(0, 16)?.replace("T", " ")}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                                    data-testid={`gemini-delete-${s.id}`}
                                    className="opacity-0 group-hover:opacity-100 text-ink-faint hover:text-stamp-red"
                                    title="Delete chat"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </aside>

            {/* Main chat pane */}
            <main className="flex-1 flex flex-col min-w-0">
                <header className="p-3 border-b border-ink/15 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-stamp-red" />
                    <h1 className="font-heading uppercase tracking-[0.22em] text-sm text-ink">Gemini</h1>
                    <span className="font-mono text-[10px] text-ink-faint">gemini-3.1-pro-preview</span>
                    {/* Iter 31.315 — Send to Two: files the current transcript
                        as a canonical `gemini_handoff` ledger entry so Two
                        (Claude via MCP) can pick it up and act on it. */}
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handoff}
                            disabled={!activeId || messages.length === 0 || handoffState?.loading}
                            data-testid="gemini-handoff-to-two"
                            title="File this transcript for Two to pick up via MCP"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-blue text-stamp-blue hover:bg-stamp-blue/10 disabled:opacity-40 inline-flex items-center gap-1.5"
                        >
                            {handoffState?.loading
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <ArrowRightCircle className="w-3 h-3" />}
                            Send to Two
                        </button>
                    </div>
                </header>

                {handoffState?.ok ? (
                    <div className="px-4 py-3 border-b border-stamp-blue/40 bg-stamp-blue/5 text-ink" data-testid="gemini-handoff-ok">
                        <div className="font-heading uppercase tracking-[0.22em] text-[10px] text-stamp-blue mb-1">
                            Filed · {handoffState.turn_count} turn{handoffState.turn_count === 1 ? "" : "s"}
                        </div>
                        <div className="font-mono text-[10px] text-ink-soft mb-1">
                            handoff_id: <span data-testid="gemini-handoff-id" className="text-ink">{handoffState.handoff_id}</span>
                        </div>
                        <div className="font-body text-[11px] text-ink-soft leading-relaxed">
                            {handoffState.msg}
                        </div>
                    </div>
                ) : null}
                {handoffState?.err ? (
                    <div className="px-4 py-2 text-[11px] text-stamp-red font-body border-b border-stamp-red/40" data-testid="gemini-handoff-err">
                        Handoff failed: {handoffState.msg}
                    </div>
                ) : null}

                {!activeId ? (
                    <div className="flex-1 flex items-center justify-center text-ink-faint font-body text-[12px]" data-testid="gemini-empty">
                        <div className="text-center">
                            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                            <div>Select a chat or start a new one.</div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-4 space-y-4"
                            data-testid="gemini-messages"
                        >
                            {messages.map((m) => (
                                <div key={m.id || `${m.role}-${m.ts}`} className={m.role === "user" ? "text-right" : ""}>
                                    <div className={`inline-block max-w-[80%] px-3 py-2 border font-body text-[12px] leading-relaxed whitespace-pre-wrap text-left ${m.role === "user" ? "border-stamp-red/40 bg-stamp-red/5 text-ink" : "border-ink/20 bg-paper-light text-ink"}`}
                                         data-testid={`gemini-msg-${m.role}`}>
                                        {m.content}
                                    </div>
                                </div>
                            ))}
                            {pending ? (
                                <div>
                                    <div className="inline-block max-w-[80%] px-3 py-2 border border-ink/20 bg-paper-light font-body text-[12px] leading-relaxed whitespace-pre-wrap"
                                         data-testid="gemini-pending">
                                        {pending}
                                        <span className="animate-pulse text-stamp-red ml-1">▊</span>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        {err ? (
                            <div className="px-4 py-2 text-[11px] text-stamp-red font-body border-t border-stamp-red/40" data-testid="gemini-err">
                                {err}
                            </div>
                        ) : null}
                        <div className="p-3 border-t border-ink/15 flex items-end gap-2">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        send();
                                    }
                                }}
                                placeholder="Message Gemini · Enter to send, Shift+Enter for newline"
                                rows={2}
                                data-testid="gemini-input"
                                className="flex-1 px-3 py-2 border border-ink/30 bg-paper-light font-mono text-[12px] text-ink resize-none"
                                disabled={streaming}
                            />
                            <button
                                type="button"
                                onClick={send}
                                disabled={!input.trim() || streaming}
                                data-testid="gemini-send"
                                className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-40 inline-flex items-center gap-1.5"
                            >
                                {streaming ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Send
                            </button>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
